import { getPreferredBookmaker } from "../components/lib/oddsGames";
import { formatOdds, formatPoint, formatTotalPoint } from "./odds";
import {
  findDrawOutcome,
  getDrawDisplayLabel,
  resolveH2hSides,
} from "./h2hOutcomes";
import { getFeaturedMarketLabel } from "./complianceCopy";
import { isDTS } from "./platform";

const featuredBoardHeaders = () => [
  "Spread",
  getFeaturedMarketLabel("h2h", isDTS()),
  "Total",
];

const EMPTY_CELL = { display: "—", hasValue: false };

const findOutcomeForTeam = (market, teamName) =>
  market?.outcomes?.find((o) => o.name === teamName);

const findMarket = (bookmaker, key) =>
  bookmaker?.markets?.find((m) => m.key === key) || null;

const buildSpreadCell = (market, teamName) => {
  const outcome = findOutcomeForTeam(market, teamName);
  if (!outcome) return { ...EMPTY_CELL };
  const display = formatSpreadLine(outcome);
  return {
    hasValue: Boolean(display),
    point: formatPoint(outcome.point),
    price: formatOdds(outcome.price),
    display: display || "—",
  };
};

const buildH2hCell = (market, teamName) => {
  const outcome = findOutcomeForTeam(market, teamName);
  if (!outcome || typeof outcome.price !== "number") return { ...EMPTY_CELL };
  return {
    hasValue: true,
    display: formatOdds(outcome.price),
  };
};

const buildH2hDrawCell = (market) => {
  const { outcome } = findDrawOutcome(market);
  if (!outcome || typeof outcome.price !== "number") return { ...EMPTY_CELL };
  return {
    hasValue: true,
    label: getDrawDisplayLabel(outcome.name),
    display: formatOdds(outcome.price),
  };
};

const buildTotalCell = (market, selectionName) => {
  const outcome = findOutcomeForTeam(market, selectionName);
  if (!outcome) return { ...EMPTY_CELL };
  const line = formatTotalPoint(outcome.point, outcome.name);
  return {
    hasValue: true,
    point: line,
    label: outcome.name,
    price: formatOdds(outcome.price),
    display: `${line} (${formatOdds(outcome.price)})`,
  };
};

/** Full Spread / M-Line / Total grid for favorites & recents sidebar cards. */
export const getMatchOddsGridDisplay = (item) => {
  const bookmaker = getPreferredBookmaker(item?.bookmakers);
  const homeTeam = item?.home_team;
  const awayTeam = item?.away_team;

  if (!bookmaker || !homeTeam || !awayTeam) {
    return {
      hasOdds: false,
      headers: featuredBoardHeaders(),
      rows: [],
    };
  }

  const spreads = findMarket(bookmaker, "spreads");
  const h2h = findMarket(bookmaker, "h2h");
  const totals = findMarket(bookmaker, "totals");

  const h2hSides = resolveH2hSides(h2h, homeTeam, awayTeam);

  const rows = [
    {
      spreads: buildSpreadCell(spreads, homeTeam),
      h2h: buildH2hCell(h2h, homeTeam),
      totals: buildTotalCell(totals, "Over"),
    },
  ];

  if (h2hSides.isThreeWay) {
    rows.push({
      spreads: { ...EMPTY_CELL },
      h2h: buildH2hDrawCell(h2h),
      totals: { ...EMPTY_CELL },
    });
  }

  rows.push({
    spreads: buildSpreadCell(spreads, awayTeam),
    h2h: buildH2hCell(h2h, awayTeam),
    totals: buildTotalCell(totals, "Under"),
  });

  const hasOdds = rows.some(
    (row) => row.spreads.hasValue || row.h2h.hasValue || row.totals.hasValue
  );

  return {
    hasOdds,
    headers: featuredBoardHeaders(),
    rows,
  };
};

/** Prefer moneyline (h2h); fall back to spread when bookmaker omits h2h. */
export const getPrimaryMarket = (item) => {
  const bookmaker = getPreferredBookmaker(item?.bookmakers);
  const markets = bookmaker?.markets;
  if (!Array.isArray(markets) || markets.length === 0) return null;
  return (
    markets.find((m) => m.key === "h2h") ||
    markets.find((m) => m.key === "spreads") ||
    null
  );
};

export const formatSpreadLine = (outcome) => {
  if (!outcome || typeof outcome.point !== "number") return null;
  const point = formatPoint(outcome.point);
  if (typeof outcome.price === "number") {
    return `${point} (${formatOdds(outcome.price)})`;
  }
  return point;
};

/** Odds summary for favorites sidebar cards and accordion rows. */
export const getMatchOddsDisplay = (item) => {
  const market = getPrimaryMarket(item);
  if (!market || !item?.home_team || !item?.away_team) {
    return {
      marketType: null,
      homeLabel: "Home",
      awayLabel: "Away",
      homeDisplay: null,
      awayDisplay: null,
      hasOdds: false,
    };
  }

  const homeOutcome = findOutcomeForTeam(market, item.home_team);
  const awayOutcome = findOutcomeForTeam(market, item.away_team);
  const drawOutcome = findDrawOutcome(market).outcome;

  if (market.key === "h2h") {
    const homePrice = homeOutcome?.price;
    const awayPrice = awayOutcome?.price;
    const drawPrice = drawOutcome?.price;
    return {
      marketType: "h2h",
      homeLabel: "Home",
      awayLabel: "Away",
      drawLabel: drawOutcome ? getDrawDisplayLabel(drawOutcome.name) : null,
      homeDisplay: formatOdds(homePrice),
      awayDisplay: formatOdds(awayPrice),
      drawDisplay: drawOutcome ? formatOdds(drawPrice) : null,
      hasOdds:
        typeof homePrice === "number" ||
        typeof awayPrice === "number" ||
        typeof drawPrice === "number",
    };
  }

  if (market.key === "spreads") {
    const homeDisplay = formatSpreadLine(homeOutcome);
    const awayDisplay = formatSpreadLine(awayOutcome);
    return {
      marketType: "spreads",
      homeLabel: "Home",
      awayLabel: "Away",
      homeDisplay,
      awayDisplay,
      hasOdds: Boolean(homeDisplay || awayDisplay),
    };
  }

  return {
    marketType: null,
    homeLabel: "Home",
    awayLabel: "Away",
    homeDisplay: null,
    awayDisplay: null,
    hasOdds: false,
  };
};
